"use client";

import { create } from "zustand";
import { UseMutationResult } from "@tanstack/react-query";
import { TradeListType } from "modules/tradeLists/model/tradeList"; // ============================================================================

// ============================================================================
// Types
// ============================================================================

type ServerActionWithId = {
  id: string;
  action: (id: string, req: any, type?: TradeListType) => Promise<any>;
  req: any;
  type?: TradeListType;
  after?: () => void;
};

type ServerActionWithoutId = {
  action: (type: TradeListType, req: any) => Promise<any>;
  req: any;
  type: TradeListType;
  after?: () => void;
};

type ServerAction = ServerActionWithId | ServerActionWithoutId;

export interface ConfirmDeleteModalState {
  open: boolean;
  name: string;
  confirmFunc?: any;
  loadingButton?: boolean;
  mutation?: {
    op: UseMutationResult<any, unknown, any, unknown>;
    req: any;
  };
  serverAction?: ServerAction;
  after?: () => void;
}

// ============================================================================
// Store Interface
// ============================================================================

interface ConfirmDeleteStore {
  // Modal State
  modal: ConfirmDeleteModalState;

  // Actions
  setModal: (modal: ConfirmDeleteModalState) => void;
  openModal: (config: Omit<ConfirmDeleteModalState, "open">) => void;
  closeModal: () => void;

  // Reset
  reset: () => void;
}

// ============================================================================
// Default State
// ============================================================================

const DEFAULT_MODAL: ConfirmDeleteModalState = {
  open: false,
  name: "",
  confirmFunc: null,
  loadingButton: false,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useConfirmDeleteStore = create<ConfirmDeleteStore>((set) => ({
  // Initial State
  modal: DEFAULT_MODAL,

  // Actions
  setModal: (modal) => set({ modal }),

  openModal: (config) =>
    set({
      modal: {
        ...config,
        open: true,
      },
    }),

  closeModal: () =>
    set((state) => ({
      modal: {
        ...state.modal,
        open: false,
      },
    })),

  // Reset All State
  reset: () => set({ modal: DEFAULT_MODAL }),
}));

// ============================================================================
// Semantic Selector Hook (Exported from same file - NO wrapper files!)
// ============================================================================

/**
 * Hook for confirm delete modal state and actions
 * Used by: ConfirmDeleteModal component, delete action buttons across modules
 */
export const useConfirmDeleteModal = () => {
  const modal = useConfirmDeleteStore((state) => state.modal);
  const setModal = useConfirmDeleteStore((state) => state.setModal);
  const openModal = useConfirmDeleteStore((state) => state.openModal);
  const closeModal = useConfirmDeleteStore((state) => state.closeModal);

  return { modal, setModal, openModal, closeModal };
};
